import type { MetaDescriptor } from "@tanstack/router-core";

import type { BlogArticle, BlogAuthor, BlogCategory, BlogIndex } from "../types";
import { blogArticlePath, blogArticleUrl, blogCategoryPath } from "./blog-urls";
import { blogWordCount } from "./headings";
import { localizePath } from "@/lib/locale";
import { brandedTitle, SITE_NAME } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";
import { OG_LOCALES, BlogAuthorKind, DEFAULT_LANGUAGE, appLanguageOf, type AppLanguage } from "@complete-athlete/enums";

type HeadLink = Record<string, string>;
type HeadMeta = MetaDescriptor;

/** The page's title (with the platform's name after it) and description, for the page and its link preview. */
const titleMeta = (title: string, description: string): HeadMeta[] => [
  { title: brandedTitle(title) },
  { property: "og:title", content: title },
  ...(description
    ? [
        { name: "description", content: description },
        { property: "og:description", content: description },
      ]
    : []),
];

/** The blog's own pages on this site, in the language the reader reads the site in. */
export const blogPagePath = (path: "" | `/${string}`) => localizePath(`/blog${path}`, getLocale());

const rssLink = (origin: string): HeadLink => ({
  rel: "alternate",
  type: "application/rss+xml",
  title: m.blog_rss_title(),
  href: `${origin}${blogPagePath("/rss.xml")}`,
});

type LdValue = string | number | boolean | null | LdValue[] | { [key: string]: LdValue };
type LdObject = { [key: string]: LdValue };

/** A schema.org object without the fields left empty, which JSON-LD has no use for. */
const ld = (fields: Record<string, LdValue | undefined>): LdObject =>
  Object.fromEntries(Object.entries(fields).filter((entry): entry is [string, LdValue] => entry[1] !== undefined));

/** An author as structured data: a person with their profiles, or the company itself. */
const authorLd = (author: BlogAuthor, origin: string) =>
  ld({
    "@type": author.kind === BlogAuthorKind.TEAM ? "Organization" : "Person",
    name: author.name,
    url: `${origin}${blogPagePath(`/author/${author.slug}`)}`,
    ...(author.role && author.kind !== BlogAuthorKind.TEAM ? { jobTitle: author.role } : {}),
    ...(author.avatarUrl ? { image: author.avatarUrl } : {}),
    ...(author.links.length ? { sameAs: author.links } : {}),
  });

const publisherLd = (origin: string) => ({
  "@type": "Organization",
  name: SITE_NAME,
  url: origin,
  logo: { "@type": "ImageObject", url: `${origin}/assets/android-chrome-512x512.png`, width: 512, height: 512 },
});

/**
 * Everything a crawler and a link preview read about one article: its own title and description,
 * the address search engines should index (the athlete blog's copy for a post on both), every
 * language it is written in as hreflang with x-default on the original, the Open Graph article
 * tags, and the article and its breadcrumbs as schema.org JSON-LD.
 */
export const blogArticleHead = (
  article: BlogArticle,
  { origin, blogTitle }: { origin: string; blogTitle: string },
): { meta: HeadMeta[]; links: HeadLink[] } => {
  const url = blogArticleUrl(article.canonicalUrl, article.slug, article.locale);
  const title = article.seoTitle || article.title;
  const description = article.seoDescription || article.excerpt;
  const others = article.alternates.filter((alternate) => alternate.locale !== article.locale);
  const source =
    article.alternates.find((alternate) => alternate.locale === article.sourceLocale) ?? article.alternates[0];
  const blogUrl = `${origin}${blogPagePath("")}`;

  const image = article.coverUrl
    ? [
        { property: "og:image", content: article.coverUrl },
        ...(article.coverWidth && article.coverHeight
          ? [
              { property: "og:image:width", content: String(article.coverWidth) },
              { property: "og:image:height", content: String(article.coverHeight) },
            ]
          : []),
        { property: "og:image:alt", content: article.coverAlt || article.title },
        { name: "twitter:image", content: article.coverUrl },
      ]
    : [
        // The article sets its own preview image, so without a cover it falls back to the platform's card.
        { property: "og:image", content: `${origin}/assets/og-image.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: article.title },
      ];

  return {
    meta: [
      ...titleMeta(title, description),
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:locale", content: OG_LOCALES[article.locale] },
      // Only one of a property survives the router's head, so the original language stands for the rest.
      ...(others[0] ? [{ property: "og:locale:alternate", content: OG_LOCALES[others[0].locale] }] : []),
      ...image,
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      ...(description ? [{ name: "twitter:description", content: description }] : []),
      ...(article.publishedAt ? [{ property: "article:published_time", content: article.publishedAt }] : []),
      { property: "article:modified_time", content: article.updatedAt },
      ...(article.category ? [{ property: "article:section", content: article.category.name }] : []),
      ...(article.tags[0] ? [{ property: "article:tag", content: article.tags[0] }] : []),
      ...(article.authors[0]
        ? [{ property: "article:author", content: `${origin}${blogPagePath(`/author/${article.authors[0].slug}`)}` }]
        : []),
      {
        "script:ld+json": ld({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          description: description || undefined,
          image: article.coverUrl
            ? ld({
                "@type": "ImageObject",
                url: article.coverUrl,
                width: article.coverWidth ?? undefined,
                height: article.coverHeight ?? undefined,
              })
            : undefined,
          datePublished: article.publishedAt ?? undefined,
          dateModified: article.updatedAt,
          author: article.authors.map((author) => authorLd(author, origin)),
          publisher: publisherLd(origin),
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          url,
          inLanguage: article.locale,
          articleSection: article.category?.name,
          keywords: article.tags.length ? article.tags.join(", ") : undefined,
          wordCount: blogWordCount(article.content),
          timeRequired: `PT${article.readingMinutes}M`,
          isPartOf: { "@type": "Blog", name: blogTitle, url: blogUrl },
          ...(others.length
            ? {
                workTranslation: others.map((alternate) => ({
                  "@type": "BlogPosting",
                  inLanguage: alternate.locale,
                  url: blogArticleUrl(alternate.url, alternate.slug, alternate.locale),
                })),
              }
            : {}),
        }),
      },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: SITE_NAME, item: `${origin}${localizePath("/", getLocale())}` },
            { "@type": "ListItem", position: 2, name: blogTitle, item: blogUrl },
            ...(article.category
              ? [
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: article.category.name,
                    item: `${origin}${blogPagePath(`/category/${article.category.slug}`)}`,
                  },
                ]
              : []),
            { "@type": "ListItem", position: article.category ? 4 : 3, name: article.title, item: url },
          ],
        },
      },
    ],
    links: [
      { rel: "canonical", href: url },
      ...article.alternates.map((alternate) => ({
        rel: "alternate",
        hrefLang: alternate.locale,
        href: blogArticleUrl(alternate.url, alternate.slug, alternate.locale),
      })),
      ...(source
        ? [{ rel: "alternate", hrefLang: "x-default", href: blogArticleUrl(source.url, source.slug, source.locale) }]
        : []),
      rssLink(origin),
    ],
  };
};

/**
 * The blog's front page, a shelf or an author's page. The root route already names the page's
 * address and its languages; this adds the title, the feed and the list of posts as structured data.
 */
export const blogListHead = (
  index: BlogIndex,
  {
    origin,
    title,
    description,
    blogTitle,
    path,
  }: { origin: string; title: string; description: string; blogTitle: string; path: "" | `/${string}` },
): { meta: HeadMeta[]; links: HeadLink[] } => {
  const posts = [...(index.featured ? [index.featured] : []), ...index.items];
  const url = `${origin}${blogPagePath(path)}`;
  return {
    meta: [
      ...titleMeta(title, description),
      {
        "script:ld+json": ld({
          "@context": "https://schema.org",
          "@type": path ? "CollectionPage" : "Blog",
          name: title,
          description,
          url,
          ...(path ? { isPartOf: { "@type": "Blog", name: blogTitle, url: `${origin}${blogPagePath("")}` } } : {}),
          publisher: publisherLd(origin),
          mainEntity: {
            "@type": "ItemList",
            itemListElement: posts.map((post, order) => ({
              "@type": "ListItem",
              position: order + 1,
              url: `${origin}${blogArticlePath(post.slug, post.locale)}`,
              name: post.title,
            })),
          },
        }),
      },
    ],
    links: [rssLink(origin)],
  };
};

/**
 * A shelf's own canonical and hreflang links: it has an address of its own in each language
 * (`/blog/category/training`, `/pl/blog/category/trening`), so the site-wide ones, which swap
 * only the prefix, would point at addresses that redirect. x-default is the English page.
 */
export const blogCategoryLinks = (category: BlogCategory, origin: string): HeadLink[] => {
  const here = appLanguageOf(getLocale()) ?? DEFAULT_LANGUAGE;
  const urlIn = (locale: AppLanguage) => {
    const alternate = category.alternates.find((item) => item.locale === locale);
    return `${origin}${blogCategoryPath(alternate?.slug ?? category.slug, locale)}`;
  };
  return [
    { rel: "canonical", href: urlIn(here) },
    ...category.alternates.map((alternate) => ({
      rel: "alternate",
      hrefLang: alternate.locale,
      href: urlIn(alternate.locale),
    })),
    { rel: "alternate", hrefLang: "x-default", href: urlIn(DEFAULT_LANGUAGE) },
  ];
};
